// src/pages/ResetPassword.js
import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/NotificationContainer';
import { analyticsEvents } from '../utils/analyticsEvents';

const ResetPassword = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { notifications, showSuccess, showError, removeNotification } = useNotification();

  useEffect(() => {
    if (!token) {
      showError(t('auth.invalidResetToken', 'Invalid or expired reset link. Please request a new one.'));
    }
  }, [token, showError, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      showError(t('auth.passwordsDoNotMatch', 'Passwords do not match'));
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess(true);
      analyticsEvents.resetPasswordSuccess();
      showSuccess(t('auth.passwordResetSuccess', 'Your password has been reset. You can now log in.'));
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Reset failed';
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-shayek-light-50 to-shayek-light-100 dark:from-shayek-dark dark:to-shayek-dark-50 px-4 transition-colors">
        <NotificationContainer notifications={notifications} onRemove={removeNotification} />
        <div className="max-w-md w-full bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek-lg p-8 border border-shayek-light-300 dark:border-shayek-dark-200 text-center">
          <p className="text-shayek-light-700 dark:text-shayek-dark-300 mb-4">{t('auth.invalidResetToken', 'Invalid or expired reset link. Please request a new one.')}</p>
          <Link to="/forgot-password" className="text-shayek-primary dark:text-shayek-red-light font-medium">{t('auth.requestNewLink', 'Request a new link')}</Link>
          <span className="mx-2">|</span>
          <Link to="/login" className="text-shayek-primary dark:text-shayek-red-light font-medium">{t('auth.backToLogin', 'Back to sign in')}</Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-shayek-light-50 to-shayek-light-100 dark:from-shayek-dark dark:to-shayek-dark-50 px-4 transition-colors">
        <NotificationContainer notifications={notifications} onRemove={removeNotification} />
        <div className="max-w-md w-full bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek-lg p-8 border border-shayek-light-300 dark:border-shayek-dark-200 text-center">
          <p className="text-green-600 dark:text-green-400 font-medium mb-4">{t('auth.passwordResetSuccess', 'Your password has been reset. You can now log in.')}</p>
          <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">Redirecting to sign in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-shayek-light-50 to-shayek-light-100 dark:from-shayek-dark dark:to-shayek-dark-50 px-4 transition-colors">
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />
      <div className="max-w-md w-full bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek-lg p-8 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
        <h2 className="text-2xl font-bold text-center text-shayek-light-900 dark:text-shayek-dark-900 mb-6">
          {t('auth.setNewPassword', 'Set new password')}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
              {t('auth.newPassword', 'New password')}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
              {t('auth.confirmPassword', 'Confirm password')}
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4 bg-shayek-primary dark:bg-shayek-red-light text-white font-medium rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-dark disabled:opacity-50 transition-colors"
          >
            {loading ? t('common.loading', 'Saving...') : t('auth.resetPassword', 'Reset password')}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-shayek-light-600 dark:text-shayek-dark-600">
          <Link to="/login" className="text-shayek-primary hover:text-shayek-primary-600 dark:text-shayek-red-light dark:hover:text-shayek-red-dark font-medium transition-colors">
            {t('auth.backToLogin', 'Back to sign in')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
